import React from 'react'

export type UseDebouncedStateParams<T, Update extends T = T> = {
  value: T
  onChange: (
    value: Update,
    p: { onSettled: () => void; onError: () => void },
  ) => void
  delay?: number
}

/**
 * Behaves similarly to React.useState, but the returned value will be forced to the passed value
 * when there is no local changes in progress and the value has changed.
 * Use it to synchronize state from the server while allowing efficient local changes and
 * avoiding too many mutations
 * @param onChange Function to call after the delay period (usually a mutation)
 * @param value The value from the server, ignored if there is newer local changes
 * @param delay debounce delay
 */
export const useDebouncedState = <T, Update extends T = T>({
  onChange,
  value,
  delay = 800,
}: UseDebouncedStateParams<T, Update>): [T, (v: Update) => void] => {
  const [internalValue, setInternalValue] = React.useState(value)
  const isChanging = React.useRef<true[]>([])
  const debounced = React.useRef<NodeJS.Timeout | null>(null)

  const cancelChanges = () => {
    if (debounced.current !== null) {
      clearTimeout(debounced.current)
    }
  }

  const exposedOnChange = (t: Update) => {
    isChanging.current.push(true)
    setInternalValue(t)
    cancelChanges()
    debounced.current = setTimeout(() => {
      onChange(t, {
        onSettled: () => isChanging.current.pop(),
        // if the onChange(usually a query) fails,
        // we restore the external value because the local value may be wrong
        onError: () => setInternalValue(value),
      })
      debounced.current = null
    }, delay)
  }

  React.useEffect(() => cancelChanges, [])
  React.useEffect(() => {
    if (isChanging.current.length === 0) {
      setInternalValue(value)
    }
  }, [value])

  return [internalValue, exposedOnChange]
}
